import React, { useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import styled from "@emotion/styled";
import { API } from "../API";
import { PatientTaskInstanceId } from "../messages/ObjectIds";
import { STRINGS } from "../strings";
import { translate } from "../translate";
import Spinner from "./Generic/Spinner";
import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitErrorHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { shakeElement } from "../utils/shakeElement";
import { UnscheduleReason } from "../messages/Caregiver";
const zFormSchema = z
  .object({
    selectedReason: z.string().min(1),
    otherReason: z.string().nullable(),
  })
  .refine(
    (data) => {
      if (data.selectedReason === "Other") {
        return data.otherReason !== null && data.otherReason !== "";
      }
      return true;
    },
    {
      message: "Please specify a reason when selecting 'Other'",
      path: ["otherReason"],
    }
  );

interface UnscheduleReasonSelectProps {
  api: API;
  patientTaskInstanceId: PatientTaskInstanceId;
  onValidSubmit: (reason: UnscheduleReason) => void;
}

const UnscheduleReasonSelectActivity: React.FC<UnscheduleReasonSelectProps> = (
  props: UnscheduleReasonSelectProps
) => {
  const reasonRef = useRef<HTMLDivElement>(null);
  const otherReasonRef = useRef<HTMLDivElement>(null);

  const getUnscheduleReasonsQuery = useQuery({
    queryKey: ["unschedule-reasons", props.patientTaskInstanceId],
    queryFn: () => props.api.getUnscheduleReasons(props.patientTaskInstanceId),
  });

  const form = useForm({
    resolver: zodResolver(zFormSchema),
    defaultValues: { selectedReason: "", otherReason: null },
    mode: "onChange",
  });

  const selected = form.watch("selectedReason");

  const handleInvalidSubmit: SubmitErrorHandler<z.infer<typeof zFormSchema>> = (errors) => {
    if (errors.selectedReason) {
      shakeElement(reasonRef);
    }
    if (errors.otherReason) {
      shakeElement(otherReasonRef);
    }
  };

  const handleSubmit = (data: z.infer<typeof zFormSchema>) => {
    const reason = getUnscheduleReasonsQuery.data?.reasons.find(
      (reason) => reason.text === data.selectedReason
    );
    if (reason === undefined) {
      console.error("Reason is undefined");
      return;
    }
    props.onValidSubmit({
      id: reason.id,
      text: data.otherReason ?? reason.text,
    });
  };

  if (getUnscheduleReasonsQuery.status === "loading") {
    return <Spinner size={16} />;
  }

  if (getUnscheduleReasonsQuery.status === "error") {
    return <>{translate(STRINGS.SOMETHING_WENT_WRONG)}</>;
  }

  return (
    <StyledForm onSubmit={form.handleSubmit(handleSubmit, handleInvalidSubmit)}>
      <div ref={reasonRef}>
        <StyledSelect {...form.register("selectedReason")}>
          <option value="">Please select a reason</option>
          {getUnscheduleReasonsQuery.data.reasons.map((r) => (
            <option key={`${r.id}`} value={r.text}>
              {r.text}
            </option>
          ))}
        </StyledSelect>
      </div>

      <AnimatedDiv show={selected === "Other"} ref={otherReasonRef}>
        <StyledInput
          {...form.register("otherReason")}
          placeholder={translate(STRINGS.PLEASE_SPECIFY)}
        />
      </AnimatedDiv>

      <StyledSubmitButton onClick={form.handleSubmit(handleSubmit, handleInvalidSubmit)}>
        {translate(STRINGS.REMOVE_TASK)}
      </StyledSubmitButton>
    </StyledForm>
  );
};

const StyledSelect = styled.select`
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid var(--tw-slate-300);
  background-color: white;
  font-size: 14px;
  color: var(--tw-slate-700);
  margin-bottom: 10px;

  &:focus {
    outline: none;
    border-color: var(--tw-blue-500);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
  }

  &:hover {
    border-color: var(--tw-slate-400);
  }

  option {
    padding: 8px;
  }
`;

const StyledInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid var(--tw-slate-300);
  background-color: white;
  font-size: 14px;
  color: var(--tw-slate-700);
  margin-bottom: 10px;

  &:focus {
    outline: none;
    border-color: var(--tw-blue-500);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
  }

  &:hover {
    border-color: var(--tw-slate-400);
  }
`;

const AnimatedDiv = styled.div<{ show: boolean }>`
  max-height: ${(p) => (p.show ? `100%` : `0`)};
  opacity: ${(p) => (p.show ? 1 : 0)};
  width: 100%;
  overflow: hidden;
  transform: translateZ(0);
  will-change: transform, opacity;
  animation: ${(p) => (p.show ? "fadeIn" : "fadeOut")} 0.3s ease forwards;
`;

const StyledForm = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const StyledSubmitButton = styled.span`
  display: inline-block;
  padding: 10px 20px;
  background-color: var(--tw-blue-500);
  color: white;
  border-radius: 8px;
  text-align: center;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--tw-blue-600);
  }
`;

export default UnscheduleReasonSelectActivity;
